import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Divider,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
  Stack,
  Chip,
  IconButton,
  InputAdornment,
  Button
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  Money as MoneyIcon,
  SwapHoriz as SwapHorizIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';

import { postApi } from 'views/services/api';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_ICONS,
  METHODS_REQUIRING_TRANSACTION,
  METHODS_REQUIRING_BATCH,
  BASE_CURRENCY,
  emptySplitPayment
} from './constants';
import { toCents, fromCents, formatAmount, equalsAmount, subtractCents } from './utils/money';
import {
  GRADIENT_PRIMARY,
  SHADOW_CARD,
  TRANSITION_DEFAULT,
  COLOR_BORDER_NEUTRAL
} from './theme';

// ---------------------------------------------------------------------------
// Subcomponentes locales — extraídos para que el render principal sea legible.
// ---------------------------------------------------------------------------

const ModeCard = ({ active, color, icon, title, subtitle, onClick }) => (
  <Paper
    elevation={active ? 6 : 1}
    sx={{
      p: 3,
      cursor: 'pointer',
      border: active ? `3px solid ${color}` : `2px solid ${COLOR_BORDER_NEUTRAL}`,
      bgcolor: active ? '#f3f4ff' : 'white',
      transition: TRANSITION_DEFAULT,
      '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
    }}
    onClick={onClick}
  >
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <Avatar sx={{ bgcolor: active ? color : COLOR_BORDER_NEUTRAL, width: 56, height: 56 }}>{icon}</Avatar>
      <Box flex={1}>
        <Typography variant="h6" fontWeight="bold" color={active ? 'primary.main' : 'text.secondary'}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {active && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />}
    </Box>
  </Paper>
);

const PaymentMethodSelect = ({ value, onChange, displayEmpty = true, size = 'small' }) => (
  <Select value={value || ''} onChange={onChange} size={size} displayEmpty={displayEmpty} sx={{ bgcolor: 'white' }}>
    {displayEmpty && (
      <MenuItem value="">
        <em>Seleccionar</em>
      </MenuItem>
    )}
    {Object.values(PAYMENT_METHODS).map((method) => (
      <MenuItem key={method} value={method}>
        {PAYMENT_METHOD_ICONS[method]} {PAYMENT_METHOD_LABELS[method]}
      </MenuItem>
    ))}
  </Select>
);

const TransactionField = ({ value, onChange, label = 'Número de Transacción' }) => (
  <FormControl fullWidth>
    <FormLabel sx={{ mb: 1, fontWeight: 600 }}>{label}</FormLabel>
    <TextField
      value={value || ''}
      onChange={onChange}
      placeholder="Ingrese el número de transacción"
      size="small"
      sx={{ bgcolor: 'white' }}
    />
  </FormControl>
);

const BatchField = ({ value, onChange, label = 'Número de Lote' }) => (
  <FormControl fullWidth>
    <FormLabel sx={{ mb: 1, fontWeight: 600 }}>{label}</FormLabel>
    <TextField
      value={value || ''}
      onChange={onChange}
      placeholder="Ingrese el número de lote"
      size="small"
      sx={{ bgcolor: 'white' }}
    />
  </FormControl>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const PaymentDetailsStep = ({
  values,
  setFieldValue,
  showSplitPayments,
  setShowSplitPayments,
  splitPayments,
  setSplitPayments,
  amount,
  currency,
  exchangeRates,
  handleTotalAmount,
  // Estos hooks vienen del nuevo useReservationPricing. Cuando este componente
  // se reutiliza desde PendingPaymentModal (que aún tiene su propio estado),
  // pueden venir undefined; los defaulteamos a no-op para no romper la
  // retrocompatibilidad.
  syncTotalAmount = () => {},
  syncForeignCurrencyAmount = () => {},
  handleAddSplitPayment,
  handleRemoveSplitPayment = () => {},
  handleUpdateSplitPayment,
  getTotalPaid,
  promoCode,
  setPromoCode,
  discountAmount,
  finalAmount
}) => {
  const [promoInput, setPromoInput] = React.useState('');
  const [promoError, setPromoError] = React.useState('');
  const [isValidatingPromo, setIsValidatingPromo] = React.useState(false);

  const advanceCents = toCents(values.advanceAmount);
  const finalCents = toCents(finalAmount);
  const remainingCents = subtractCents(finalCents, advanceCents);

  const advanceMatchesPaid = equalsAmount(toCents(getTotalPaid()), advanceCents);
  const advanceValue = parseFloat(values.advanceAmount || 0);
  const paidValue = getTotalPaid();

  // -------------------------------------------------------------------------
  // Sincronización de totalAmount tras cambios en finalAmount o moneda.
  //
  // Importante: la lógica de cálculo vive en useReservationPricing. Aquí
  // solo notificamos al hook que sincronice. Esto elimina la carrera entre
  // el onChange del input y el effect que provocaba el bug 10 -> 9.
  // -------------------------------------------------------------------------
  React.useEffect(() => {
    syncTotalAmount(values.advanceAmount, setFieldValue);
    syncForeignCurrencyAmount(values.paymentCurrency, exchangeRates, setFieldValue);
    // Solo nos importa reaccionar a cambios de finalAmount, paymentCurrency
    // y al modo split. El advanceAmount se gestiona en su propio onChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalAmount, values.paymentCurrency, showSplitPayments]);

  // -------------------------------------------------------------------------
  // Promo code
  // -------------------------------------------------------------------------
  const applyPromoCode = async () => {
    setPromoError('');
    if (!promoInput.trim()) return;

    try {
      setIsValidatingPromo(true);
      const hotelId = JSON.parse(localStorage.getItem('hotelData'))?.hotelId;
      const response = await postApi('api/promocode/validate', { code: promoInput, hotelId });

      if (response.status === 200 && response.data.valid) {
        setPromoCode(response.data.data);
      } else {
        setPromoError(response.data.message || 'Código inválido');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error validating promo code:', error);
      setPromoError(error.response?.data?.message || 'Error al validar el código');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode(null);
    setPromoInput('');
  };

  // -------------------------------------------------------------------------
  // Handlers de modo de pago
  // -------------------------------------------------------------------------
  const switchToSimpleMode = () => {
    if (showSplitPayments) {
      setShowSplitPayments(false);
      setSplitPayments([]);
    }
  };

  const switchToSplitMode = () => {
    if (showSplitPayments) return;
    setShowSplitPayments(true);
    if (!values.advanceAmount && finalCents > 0) {
      setFieldValue('advanceAmount', formatAmount(finalCents));
    }
    setSplitPayments([emptySplitPayment()]);
  };

  return (
    <Card sx={{ mb: 3, boxShadow: SHADOW_CARD }}>
      <Box sx={{ background: GRADIENT_PRIMARY, p: 2.5, color: 'white' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
            <CreditCardIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Información de Pago
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Configure el método de pago para esta reserva
            </Typography>
          </Box>
        </Box>
      </Box>

      <CardContent sx={{ p: 3 }}>
        {/* Selector modo de pago */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Seleccione el tipo de pago:
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <ModeCard
                active={!showSplitPayments}
                color="#667eea"
                icon={<CreditCardIcon sx={{ fontSize: 28 }} />}
                title="Pago Simple"
                subtitle="Un solo método de pago"
                onClick={switchToSimpleMode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ModeCard
                active={showSplitPayments}
                color="#4caf50"
                icon={<MoneyIcon sx={{ fontSize: 28 }} />}
                title="Pagos Múltiples"
                subtitle="Dividir en varios pagos"
                onClick={switchToSplitMode}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {!showSplitPayments && (
          <SimplePaymentSection
            values={values}
            setFieldValue={setFieldValue}
            exchangeRates={exchangeRates}
            finalCents={finalCents}
          />
        )}

        {showSplitPayments && (
          <SplitPaymentSection
            splitPayments={splitPayments}
            exchangeRates={exchangeRates}
            advanceValue={advanceValue}
            paidValue={paidValue}
            advanceMatchesPaid={advanceMatchesPaid}
            onAdd={handleAddSplitPayment}
            onRemove={handleRemoveSplitPayment}
            onUpdate={handleUpdateSplitPayment}
            canRemove={splitPayments.length > 1}
          />
        )}

        <Divider sx={{ my: 4 }} />

        <AmountsSummary
          amount={amount}
          currency={currency}
          finalAmount={finalAmount}
          discountAmount={discountAmount}
          remainingCents={remainingCents}
          promoCode={promoCode}
          promoInput={promoInput}
          setPromoInput={setPromoInput}
          promoError={promoError}
          isValidatingPromo={isValidatingPromo}
          onApplyPromo={applyPromoCode}
          onRemovePromo={removePromoCode}
          showSplitPayments={showSplitPayments}
          getTotalPaid={getTotalPaid}
          values={values}
          onChangeAdvance={(e) => handleTotalAmount(e.target.value, setFieldValue)}
        />
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Secciones del componente principal
// ---------------------------------------------------------------------------

const SimplePaymentSection = ({ values, setFieldValue, exchangeRates, finalCents }) => {
  const needsBatch = METHODS_REQUIRING_BATCH.includes(values.paymentMethod);
  const needsTransaction = METHODS_REQUIRING_TRANSACTION.includes(values.paymentMethod);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
        Configuración de Pago Simple
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
              <CreditCardIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Método de Pago
            </FormLabel>
            <PaymentMethodSelect
              value={values.paymentMethod}
              onChange={(e) => setFieldValue('paymentMethod', e.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
              <SwapHorizIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Moneda de Pago
            </FormLabel>
            <Select
              value={values.paymentCurrency || BASE_CURRENCY}
              onChange={(e) => {
                const selectedCurrency = e.target.value;
                const selectedRate = exchangeRates.find((rate) => rate.abbreviation === selectedCurrency)?.rate || 1;
                const converted = (fromCents(finalCents) * selectedRate).toFixed(2);
                setFieldValue('paymentCurrency', selectedCurrency);
                setFieldValue('exchangeRateUsed', selectedRate);
                setFieldValue('amountInForeignCurrency', converted);
              }}
              size="small"
              sx={{ bgcolor: 'white' }}
            >
              <MenuItem value={BASE_CURRENCY}>{BASE_CURRENCY} (Dólar)</MenuItem>
              {exchangeRates.map((rate) => (
                <MenuItem key={rate._id} value={rate.abbreviation}>
                  {rate.abbreviation} - {rate.currency}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {values.paymentCurrency && values.paymentCurrency !== BASE_CURRENCY && (
          <>
            <Grid item xs={12} md={6}>
              <Alert severity="info" icon={<SwapHorizIcon />}>
                <Typography variant="body2">
                  <strong>Tasa de Cambio:</strong> 1 {BASE_CURRENCY} = {values.exchangeRateUsed} {values.paymentCurrency}
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Alert severity="success" icon={<AttachMoneyIcon />}>
                <Typography variant="body2">
                  <strong>Total en {values.paymentCurrency}:</strong> {values.amountInForeignCurrency}
                </Typography>
              </Alert>
            </Grid>
          </>
        )}

        {needsBatch && (
          <Grid item xs={12} md={6}>
            <BatchField value={values.batchNumber} onChange={(e) => setFieldValue('batchNumber', e.target.value)} />
          </Grid>
        )}

        {needsTransaction && (
          <Grid item xs={12} md={6}>
            <TransactionField
              value={values.transactionNumber}
              onChange={(e) => setFieldValue('transactionNumber', e.target.value)}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const SplitPaymentSection = ({
  splitPayments,
  exchangeRates,
  advanceValue,
  paidValue,
  advanceMatchesPaid,
  onAdd,
  onRemove,
  onUpdate,
  canRemove
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
      Configuración de Pagos Múltiples
    </Typography>

    <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
      Configure múltiples métodos de pago. La suma total debe coincidir con el anticipo.
    </Alert>

    <Stack spacing={2} sx={{ mb: 3 }}>
      {splitPayments.map((payment, index) => (
        <SplitPaymentRow
          key={payment.id}
          payment={payment}
          index={index}
          exchangeRates={exchangeRates}
          canRemove={canRemove}
          onRemove={() => onRemove(payment.id)}
          onUpdate={(field, value) => onUpdate(payment.id, field, value)}
        />
      ))}
    </Stack>

    <Button
      variant="outlined"
      startIcon={<AddIcon />}
      onClick={onAdd}
      fullWidth
      sx={{ mb: 3, borderStyle: 'dashed', borderWidth: 2, py: 1.5 }}
    >
      Agregar Otro Método de Pago
    </Button>

    <SplitPaymentSummary
      paidValue={paidValue}
      advanceValue={advanceValue}
      advanceMatchesPaid={advanceMatchesPaid}
      splitPayments={splitPayments}
    />
  </Box>
);

const SplitPaymentRow = ({ payment, index, exchangeRates, canRemove, onRemove, onUpdate }) => {
  const isComplete = payment.amount && payment.paymentMethod;
  const needsBatch = METHODS_REQUIRING_BATCH.includes(payment.paymentMethod);
  const needsTransaction = METHODS_REQUIRING_TRANSACTION.includes(payment.paymentMethod);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        border: '2px solid',
        borderColor: isComplete ? 'success.light' : 'grey.300',
        bgcolor: isComplete ? '#f9fafb' : 'white'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip label={`Pago ${index + 1}`} color={isComplete ? 'success' : 'default'} size="small" />
          {isComplete && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
        </Box>
        {canRemove && (
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Método</FormLabel>
            <PaymentMethodSelect value={payment.paymentMethod} onChange={(e) => onUpdate('paymentMethod', e.target.value)} />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Moneda</FormLabel>
            <Select value={payment.currency} onChange={(e) => onUpdate('currency', e.target.value)}>
              <MenuItem value={BASE_CURRENCY}>{BASE_CURRENCY}</MenuItem>
              {exchangeRates.map((rate) => (
                <MenuItem key={rate._id} value={rate.abbreviation}>
                  {rate.abbreviation}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Monto ({BASE_CURRENCY})</FormLabel>
            <TextField
              type="number"
              value={payment.amount}
              onChange={(e) => onUpdate('amount', e.target.value)}
              placeholder="0.00"
              inputProps={{ step: '0.01', min: 0 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              size="small"
            />
          </FormControl>
        </Grid>
      </Grid>

      {needsTransaction && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TransactionField
              value={payment.transactionNumber}
              onChange={(e) => onUpdate('transactionNumber', e.target.value)}
            />
          </Grid>
        </Grid>
      )}

      {needsBatch && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <BatchField value={payment.batchNumber} onChange={(e) => onUpdate('batchNumber', e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TransactionField
              value={payment.transactionNumber}
              onChange={(e) => onUpdate('transactionNumber', e.target.value)}
            />
          </Grid>
        </Grid>
      )}

      {payment.currency !== BASE_CURRENCY && payment.amount && toCents(payment.amount) > 0 && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              <Typography variant="caption">
                💱 Equivalente:{' '}
                <strong>
                  {payment.currency} {parseFloat(payment.amountInForeignCurrency || 0).toFixed(2)}
                </strong>{' '}
                (Tasa: {payment.exchangeRateUsed})
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

const SplitPaymentSummary = ({ paidValue, advanceValue, advanceMatchesPaid, splitPayments }) => {
  const difference = Math.abs(advanceValue - paidValue);
  const allMethodsComplete = splitPayments.every((p) => p.paymentMethod && parseFloat(p.amount) > 0);
  const showSuccessAlert = paidValue > 0 && advanceMatchesPaid && allMethodsComplete;
  const showMismatchAlert = paidValue > 0 && !advanceMatchesPaid;

  return (
    <Paper elevation={3} sx={{ p: 2.5, bgcolor: '#fafafa' }}>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        <ReceiptIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
        Resumen de Pagos
      </Typography>
      <Divider sx={{ my: 1.5 }} />

      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <Typography variant="body2" color="text.secondary">
            Total de Pagos:
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Typography
            variant="body2"
            fontWeight="bold"
            color={advanceMatchesPaid && paidValue > 0 ? 'success.main' : 'error.main'}
          >
            ${paidValue.toFixed(2)}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2" color="text.secondary">
            Anticipo Requerido:
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Typography variant="body2" fontWeight="bold">
            ${advanceValue.toFixed(2)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2" fontWeight="bold">
            Diferencia:
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Typography
            variant="body2"
            fontWeight="bold"
            color={advanceMatchesPaid ? 'success.main' : 'warning.main'}
          >
            ${difference.toFixed(2)}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        {paidValue === 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="caption">⚠️ Ingrese los montos en cada pago</Typography>
          </Alert>
        )}

        {showMismatchAlert && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight="bold" display="block">
              ⚠️ Acción Requerida:
            </Typography>
            <Typography variant="caption">
              {paidValue < advanceValue
                ? `Faltan $${(advanceValue - paidValue).toFixed(2)} por distribuir`
                : `Sobran $${(paidValue - advanceValue).toFixed(2)} en los pagos`}
            </Typography>
          </Alert>
        )}

        {showSuccessAlert && (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            <Typography variant="caption" fontWeight="bold">
              ✅ Pagos validados correctamente. Puede confirmar la reserva.
            </Typography>
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

const AmountsSummary = ({
  amount,
  currency,
  finalAmount,
  discountAmount,
  remainingCents,
  promoCode,
  promoInput,
  setPromoInput,
  promoError,
  isValidatingPromo,
  onApplyPromo,
  onRemovePromo,
  showSplitPayments,
  getTotalPaid,
  values,
  onChangeAdvance
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
      Resumen de Montos
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Subtotal
          </Typography>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="text.secondary"
            sx={{ textDecoration: promoCode ? 'line-through' : 'none' }}
          >
            {currency} {amount}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={3}>
        <Paper
          elevation={1}
          sx={{ p: 2, bgcolor: promoCode ? '#e8f5e9' : 'white', border: `1px solid ${COLOR_BORDER_NEUTRAL}`, minHeight: '100%' }}
        >
          {!promoCode ? (
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 600 }}>Código Promocional</FormLabel>
              <TextField
                size="small"
                disabled={isValidatingPromo}
                placeholder="Ingresar código"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                error={!!promoError}
                helperText={promoError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button onClick={onApplyPromo} disabled={isValidatingPromo || !promoInput} size="small" variant="contained">
                        Aplicar
                      </Button>
                    </InputAdornment>
                  )
                }}
              />
            </FormControl>
          ) : (
            <Box display="flex" justifyContent="space-between" alignItems="center" height="100%">
              <Box>
                <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                  Código Aplicado:
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  <LocalOfferIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} color="success" />
                  {promoCode.code}
                </Typography>
                <Typography variant="caption" color="success.main">
                  -{currency} {discountAmount.toFixed(2)}
                </Typography>
              </Box>
              <IconButton size="small" color="error" onClick={onRemovePromo}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={2}>
        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Total a Pagar
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="success.main">
            {currency} {finalAmount.toFixed(2)}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={2}>
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
            <AttachMoneyIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
            Anticipo
          </FormLabel>
          <TextField
            name="advanceAmount"
            type="number"
            value={values.advanceAmount}
            onChange={onChangeAdvance}
            placeholder="0.00"
            size="small"
            // step="0.01" desbloquea la entrada de decimales (default HTML
            // es step="1", lo que rechaza valores como 10.25 en algunos browsers).
            inputProps={{ step: '0.01', min: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText={showSplitPayments && getTotalPaid() > 0 ? `Total distribuido: $${getTotalPaid().toFixed(2)}` : ''}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={2}>
        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Restante
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="warning.main">
            {currency} {fromCents(remainingCents).toFixed(2)}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

export default PaymentDetailsStep;
